// noinspection JSUnusedGlobalSymbols
import {defineStore} from "pinia";
import checkoutService from "@/checkout/services/checkout.service.ts";
import * as Sentry from "@sentry/vue";

import {
    Account,
    Address,
    AddressInput,
    App,
    CheckoutAccountIdentity,
    CheckoutConfig,
    CheckoutCustomerIdentity,
    CheckoutDeliveryMethod,
    CheckoutInput,
    CheckoutResponse,
    CheckoutValidation,
    Customer,
    DefaultDeliveryMethod,
    DeliveryMethod,
    DeliveryType,
    LineItem,
    Payment,
    PaymentMethod,
    PickupDeliveryMethod,
    TenantSettings,
    TotalCost
} from "@/shared/graphql/types"
import {OnBehalfOf} from "@/shared/composable/security.ts";
import {Locale} from "@/checkout/i18n/i18n.ts";

let onBeHalfOf: OnBehalfOf = null
export const useCheckoutStore = defineStore('checkout', {
    state: (): CheckoutState => ({
        initialized: false,
        updating: false,
        expired: false,
        serverError: false,
        loading: true,
        busy: false,
        valid: false,
        username: "",
        id: "",
        locale: new Locale("nl", "BE"),
        cost: null,
        customer: null,
        account: null,
        customerIdentity: null,
        shippingType: ShippingType.PICKUP,
        items: [],
        paymentMethods: [],
        paymentMethod: null,
        payment: null,
        deliveryMethods: [],
        deliveryMethod: null,
        deliveryTypes: [],
        preferredDeliveryDate: null,
        deliveryAddresses: [],
        deliveryAddress: null,
        deliveryAddressesInput: null,
        billingAddress: null,
        billingAddressInput: null,
        customerRefRemarkOpen: false,
        customerReference: "",
        remark: "",
        config: null,
        totalItemQuantity: 0,
        validation: null,
        selectedAddressId: null,
        displayVatTotals: true,
        paysVat: true,
        app: null,
        settings: null
    }),

    getters: {
        isGuestBasket: (state) => state.customer == null,
        isSalesRepAccount: (state): boolean => {
            return (state?.account["__typename"] == "SalesRepOnlyAccount")
        },
    },

    actions: {
        async updateCheckout(): Promise<CheckoutResponse> {
            return await handleResponse(this, checkoutService.update(this.id, createCheckoutInput(this), onBeHalfOf))
        },

        async updatePaymentMethod(method: PaymentMethod): Promise<CheckoutResponse> {
            this.paymentMethod = method
            return await this.updateCheckout()
        },

        async updateDeliveryMethod(method: DeliveryMethod): Promise<CheckoutResponse> {
            this.deliveryMethod = this.deliveryMethods.find((m: CheckoutDeliveryMethod) => m.id === method.id) || null

            if (method.kind === 'PICKUP') {
                this.deliveryAddresses = (this.deliveryMethod as PickupDeliveryMethod).addresses // Cast to any as we don't have the exact type
                this.deliveryAddress = this.deliveryAddresses.find(() => true)
            } else if (method.kind === 'DELIVERY' || method.kind === 'TRANSPORT') {
                this.deliveryAddresses = (this.deliveryMethod as DefaultDeliveryMethod).addresses // Cast to any as we don't have the exact type
                const selectFallbackAddress = (): Address | null => {
                    if (this.customer?.delivery?.address != null) {
                        return this.customer.delivery.defaultAddress;
                    }
                    return this.deliveryAddresses[0] || null;
                };
                // Check if the current delivery address exists in the list, else use the fallback
                if (this.deliveryAddress) {
                    const exists = this.deliveryAddresses.some((address: Address) => address.id === this.deliveryAddress.id);
                    if (!exists) this.deliveryAddress = selectFallbackAddress();
                } else this.deliveryAddress = selectFallbackAddress();

            } else if (method.kind === 'BPOST') {
                throw Error("BPost not implemented yet")
            }
            return await this.updateCheckout()
        },

        async updateDeliveryAddress(address: Address): Promise<CheckoutResponse> {
            this.deliveryAddress = address
            return await this.updateCheckout()
        },

        async cancel(): Promise<boolean> {
            try {
                this.busy = true
                return await checkoutService.cancel(this.id, onBeHalfOf)
            } catch (e) {
                return false
            } finally {
                this.busy = false
            }
        },
        async init(checkoutId: string) {
            if (this.initialized) return
            const initCheckout = async (): Promise<CheckoutResponse> => {
                const loaded = await checkoutService.init(checkoutId, onBeHalfOf)
                this.username = loaded.account?.username || (loaded.checkoutResponse?.checkout?.customer?.email || "Guest")
                this.settings = loaded.tenantSettings
                this.account = loaded.account
                this.app = loaded.app
                return loaded.checkoutResponse as CheckoutResponse
            }
            await handleResponse(this, initCheckout())
            this.loading = false
            this.initialized = true
        },

        async complete(): Promise<CheckoutResponse> {
            const basePath = process.env.APP_BASE_PATH || '';
            const sanitizedPath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
            return handleResponse(this, checkoutService.complete(
                this.id,
                createCheckoutInput(this),
                `${window.location.origin}${sanitizedPath}/${this.id}/complete`,
                onBeHalfOf
            ))
        },
    }
})

export enum ShippingType {
    SHIP, PICKUP
}

interface CheckoutState {
    id: string;
    initialized: boolean,
    expired: boolean,
    serverError: boolean,
    loading: boolean,
    busy: boolean,
    updating: boolean;
    valid: boolean;
    locale: Locale;
    username: string;
    customerIdentity: CheckoutCustomerIdentity;
    customer: Customer;
    account: Account;
    cost: TotalCost;
    shippingType: ShippingType;
    items: LineItem[];
    paymentMethods: PaymentMethod[];
    paymentMethod: PaymentMethod | null;
    payment: Payment;
    deliveryMethods: CheckoutDeliveryMethod[];
    deliveryMethod: CheckoutDeliveryMethod | null;
    deliveryTypes: DeliveryType[] | null;
    preferredDeliveryDate: Date | null
    deliveryAddresses: Address[];
    deliveryAddressesInput: AddressInput,
    deliveryAddress: Address | null;
    billingAddress: Address | null;
    billingAddressInput: AddressInput,
    customerRefRemarkOpen: boolean;
    customerReference: string;
    config: CheckoutConfig;
    remark: string;
    totalItemQuantity: number;
    validation: CheckoutValidation;
    selectedAddressId: string | null;
    displayVatTotals: boolean;
    paysVat: boolean;
    settings: TenantSettings;
    app: App | null;
}

const createCheckoutInput = (store: CheckoutState): CheckoutInput => {
    let deliveryAddresses: AddressInput
    const isGuest = store.customer == null
    if (isGuest) {
        deliveryAddresses = store.deliveryAddressesInput
    } else {
        deliveryAddresses = {id: store.deliveryAddress?.id} as AddressInput
    }
    const input = {
        customerReference: store.customerReference,
        remark: store.remark,
        delivery: {
            address: deliveryAddresses,
            method: store.deliveryMethod!.id,
            preferredDate: store.preferredDeliveryDate,
        },
        billing: {
            method: store.paymentMethod!.code,
        },
        discountCodes: []
    } as CheckoutInput
    if (isGuest) {
        input.billing.address = store.billingAddressInput || store.deliveryAddressesInput
    }
    return input
}
// Private function outside the store
const handleResponse = async (store: CheckoutState, promise: Promise<CheckoutResponse>): Promise<CheckoutResponse> => {
    try {
        store.updating = true
        store.valid = false
        const response = await promise
        store.updating = false
        const checkout = response.checkout
        store.id = checkout.id
        store.totalItemQuantity = checkout.totalItemQuantity
        store.validation = response.validation
        store.valid = response.validation.success
        store.cost = checkout.cost
        store.locale = new Locale(checkout.language.code, "BE")
        store.paymentMethods = checkout.billing.methods
        store.paymentMethod = store.paymentMethods.find((method) => method.code === checkout.billing.method.code) || null
        store.deliveryMethods = checkout.delivery.methods
        store.deliveryTypes = checkout.delivery.types
        if (checkout.delivery.method) store.deliveryMethod = store.deliveryMethods.find((method) => method.id === checkout.delivery.method.id) || null
        store.deliveryAddresses = (store.deliveryMethod as any)?.addresses ?? [] // Cast to any as we don't have the exact type
        store.deliveryAddress = checkout.delivery.address
        store.preferredDeliveryDate = checkout.delivery.preferredDate
        checkout.delivery.address
        store.billingAddress = checkout.billing.address
        store.selectedAddressId = checkout.delivery.address?.id
        store.items = checkout.items
        store.payment = response.payment
        store.displayVatTotals = checkout.displayVatTotals
        store.paysVat = checkout.paysVat
        store.customerIdentity = checkout.customer
        store.remark = checkout.remark
        store.customerReference = checkout.customerReference
        store.config = checkout.config
        if (checkout.state.status === 'Expired') {
            store.expired = true
        }
        if (('customer' in checkout.customer)) {
            store.customer = (checkout.customer as CheckoutAccountIdentity).customer
        }
        return response
    } catch (e) {
        Sentry.captureException(e, {
            level: "error",
            tags: {
                feature: "checkout-update",
                checkoutId: store.id || "unknown"
            },
            extra: {
                customerStatus: store.customer ? "existing" : "guest",
                paymentMethod: store?.paymentMethod?.code ?? "none",
                deliveryMethod: store.deliveryMethod?.id ?? "none"
            }
        });

        store.updating = false
        store.valid = true //Let's assume it's valid here otherwise the user won't be able to retry
        store.serverError = true
        store.busy = false
        store.loading = false
        throw e
    }
}